import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Flag, ShieldAlert, Check, X, RotateCcw, Link as LinkIcon, Users, Smartphone, Monitor } from "lucide-react";
import ProtectedImage from "../components/ProtectedImage";

/**
 * Admin / Moderator inbox for Dream Team squad reports.
 * Iter 73 — listing endpoint `/api/dt/admin/reports` returns evidence
 * screenshots + the reported game number so staff can compare against
 * the player's claimed lineup.
 */
const STATUS_FILTERS = [
  { id: "open",      label: "Open",      color: "#EF4444" },
  { id: "resolved",  label: "Resolved",  color: "#10B981" },
  { id: "dismissed", label: "Dismissed", color: "#71717A" },
  { id: "",         label: "All",       color: "#7C3AED" },
];

export default function AdminDtReportsTab() {
  const [filter, setFilter] = useState("open");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/dt/admin/reports${q}`);
      setRows(data.reports || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (rid, action) => {
    setBusy(true);
    try {
      let note = "";
      if (action !== "reopen") {
        note = window.prompt(`Optional note for the ${action} action:`, "") || "";
      }
      const params = new URLSearchParams({ action });
      if (note) params.append("note", note);
      await api.post(`/dt/admin/reports/${rid}/resolve?${params.toString()}`, {});
      toast.success(`Report ${action}d.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (rows === null) {
    return <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#7C3AED]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-dt-reports">
      {/* iter 96 — registered players for the current weekly cycle */}
      <DtPlayersPanel />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-rose-500" />
          <h2 className="font-mono text-[13px] uppercase tracking-[0.25em] text-[#09090B] dark:text-[#F4F4F5]">
            Dream Team Squad Reports
          </h2>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id || "all"}
              type="button"
              onClick={() => setFilter(f.id)}
              data-testid={`admin-dt-reports-filter-${f.id || "all"}`}
              className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest border transition-colors ${
                filter === f.id
                  ? "text-white"
                  : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] border-[#E4E4E7] dark:border-[#2A2A30]"
              }`}
              style={filter === f.id ? { background: f.color, borderColor: f.color } : {}}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-[#E4E4E7] dark:border-[#2A2A30] p-10 text-center text-sm text-[#71717A]" data-testid="admin-dt-reports-empty">
          <Flag className="w-6 h-6 mx-auto mb-2 opacity-50" />
          No {filter || "Dream Team"} reports.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => <ReportRow key={r.id} r={r} onAct={act} busy={busy} />)}
        </div>
      )}
    </div>
  );
}

function ReportRow({ r, onAct, busy }) {
  const statusColor = r.status === "open" ? "bg-rose-500/15 text-rose-500 border-rose-500/40"
    : r.status === "resolved" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40"
    : "bg-zinc-500/15 text-zinc-500 border-zinc-500/40";
  return (
    <div
      data-testid={`admin-dt-report-row-${r.id}`}
      className="rounded-2xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${statusColor}`}>
              {r.status}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
              {r.pool} · {r.cycle_id} · Game {r.reported_game_num}
            </span>
            <span className="font-mono text-[10px] text-[#71717A]">
              {(r.created_at || "").slice(0, 16).replace("T", " ")}
            </span>
          </div>
          <div className="mt-1.5 text-sm">
            <span className="text-[#71717A]">@</span>
            <span className="font-bold text-[#7C3AED]">{r.reporter_username}</span>
            <span className="text-[#71717A]"> reported </span>
            <span className="text-[#71717A]">@</span>
            <span className="font-bold text-rose-500">{r.reported_username}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {r.status !== "resolved" && (
            <button
              type="button"
              onClick={() => onAct(r.id, "resolve")}
              disabled={busy}
              data-testid={`admin-dt-report-resolve-${r.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 text-emerald-600 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 disabled:opacity-50"
            >
              <Check className="w-3 h-3" /> Resolve
            </button>
          )}
          {r.status !== "dismissed" && (
            <button
              type="button"
              onClick={() => onAct(r.id, "dismiss")}
              disabled={busy}
              data-testid={`admin-dt-report-dismiss-${r.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-zinc-500/10 hover:bg-zinc-500/20 border border-zinc-500/40 text-zinc-500 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 disabled:opacity-50"
            >
              <X className="w-3 h-3" /> Dismiss
            </button>
          )}
          {r.status !== "open" && (
            <button
              type="button"
              onClick={() => onAct(r.id, "reopen")}
              disabled={busy}
              data-testid={`admin-dt-report-reopen-${r.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/40 text-rose-500 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 disabled:opacity-50"
            >
              <RotateCcw className="w-3 h-3" /> Reopen
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg bg-[#FAFAFC] dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm text-[#27272A] dark:text-[#E4E4E7] whitespace-pre-wrap break-words">
        {r.reason}
      </div>

      {r.screenshot_path && (
        <div data-testid={`admin-dt-report-evidence-${r.id}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A] mb-1">
            Evidence
          </div>
          <ProtectedImage path={r.screenshot_path} className="max-h-64 rounded-lg border border-[#E4E4E7] dark:border-[#2A2A30]" />
        </div>
      )}

      {r.admin_note && (
        <div className="text-[11px] text-[#71717A]">
          <span className="font-mono uppercase tracking-widest">Note:</span> {r.admin_note}
        </div>
      )}

      <div className="text-[10px] text-[#71717A] inline-flex items-center gap-1">
        <LinkIcon className="w-3 h-3" /> match: <code className="font-mono">{r.match_id}</code>
      </div>
    </div>
  );
}

/* =========================================================================
   DT registered-players panel — current weekly cycle (iter 96)
   Note: Dream Team assigns weekly CONTINENTAL QUOTAS, not teams, so each
   player shows their current game (1–20) + record rather than a drawn team.
   ========================================================================= */
function DtPlayersPanel() {
  const [pool, setPool] = useState("mobile");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data } = await api.get(`/dt/admin/players?pool=${pool}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setData({ players: [], count: 0 });
    }
  }, [pool]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-4 space-y-3" data-testid="admin-dt-players">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#10B981]" />
          <h3 className="font-mono text-[12px] uppercase tracking-[0.25em] text-[#09090B] dark:text-[#F4F4F5]">
            Registered Players{data ? ` · ${data.count}` : ""}
          </h3>
        </div>
        <div className="inline-flex rounded-full border border-[#E4E4E7] dark:border-[#2A2A30] p-0.5">
          {["mobile", "console"].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPool(p)}
              data-testid={`admin-dt-players-pool-${p}`}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest transition-colors ${
                pool === p ? "bg-[#10B981] text-[#04120c]" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
              }`}
            >
              {p === "mobile" ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />} {p}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <Loader2 className="w-4 h-4 animate-spin mx-auto text-[#10B981]" />
      ) : data.players.length === 0 ? (
        <div className="text-[12px] text-[#71717A] italic">— no players in this pool's current cycle yet —</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-widest text-[#71717A]">
                <th className="text-left px-2 py-1">#</th>
                <th className="text-left">Player</th>
                <th className="text-center">Game</th>
                <th className="text-center">P</th>
                <th className="text-center">W-D-L</th>
                <th className="text-center">GD</th>
                <th className="text-right px-2 py-1">Pts</th>
              </tr>
            </thead>
            <tbody>
              {data.players.map((p, i) => (
                <tr key={p.user_id} className="border-t border-[#E4E4E7] dark:border-[#2A2A30]">
                  <td className="px-2 py-1.5 font-mono text-[#71717A]">{i + 1}</td>
                  <td className="font-bold text-[#09090B] dark:text-white">@{p.username || (p.user_id || "").slice(0, 6)}</td>
                  <td className="text-center tabular-nums">{p.current_game_num ?? "—"}/20</td>
                  <td className="text-center tabular-nums">{p.played}</td>
                  <td className="text-center tabular-nums text-[#71717A]">{p.won}-{p.drawn}-{p.lost}</td>
                  <td className="text-center tabular-nums">{p.gd > 0 ? `+${p.gd}` : p.gd}</td>
                  <td className="text-right px-2 py-1.5 font-bold tabular-nums">{p.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
